import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { EmptyStateComponent, ResourcesLoadingSkeletonsComponent } from '@buspro/web/shared';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective } from 'ng-zorro-antd/page-header';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { AdjustmentRequest, GeofenceAdjustmentService, PlaceConflict } from './geofence-adjustment.service';

/**
 * Geofence Adjustments — the admin surface for stop zones that overlap.
 *
 * Two tabs on purpose, because they answer different questions:
 *  - "Needs attention" is every conflict live on a route right now, reported or not. A tenant
 *    being blocked is not a prerequisite for a problem existing.
 *  - "Requests" is what tenants have actually raised, with its history.
 *
 * The map is a LINK, never a prerequisite. The recommended radius is computed server-side from
 * every stop sharing a route, so the common case is one click; looking at the map is for when
 * someone wants to understand rather than act.
 */
@Component({
  selector: 'admin-geofence-adjustments',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzPageHeaderComponent,
    NzPageHeaderExtraDirective,
    NzSegmentedModule,
    NzTagComponent,
    NzTooltipDirective,
    EmptyStateComponent,
    ResourcesLoadingSkeletonsComponent,
  ],
  templateUrl: './geofence-adjustments.component.html',
})
export class GeofenceAdjustmentsComponent {
  private readonly service = inject(GeofenceAdjustmentService);
  private readonly notification = inject(NzNotificationService);
  private readonly modal = inject(NzModalService);
  private readonly logger = inject(NGXLogger);

  readonly tabs = ['Needs attention', 'Requests'];
  readonly tab = signal(0);

  readonly conflictsQuery = injectQuery(() => ({
    queryKey: ['geofence-conflicts'],
    queryFn: () => lastValueFrom(this.service.conflicts()),
    placeholderData: (prev: PlaceConflict[] | undefined) => prev,
  }));

  readonly requestsQuery = injectQuery(() => ({
    queryKey: ['geofence-adjustment-requests'],
    queryFn: () => lastValueFrom(this.service.requests()),
    placeholderData: (prev: AdjustmentRequest[] | undefined) => prev,
  }));

  readonly summaryQuery = injectQuery(() => ({
    queryKey: ['geofence-adjustment-summary'],
    queryFn: () => lastValueFrom(this.service.summary()),
  }));

  readonly conflicts = computed(() => this.conflictsQuery.data() ?? []);
  readonly requests = computed(() => this.requestsQuery.data() ?? []);
  readonly pending = computed(() => this.requests().filter((r) => r.status === 'PENDING'));

  /** A place already has an open request — so the row offers "view request", not "raise" again. */
  readonly openRequestByPlace = computed(
    () => new Map(this.pending().map((r) => [r.standardPlaceId, r] as const)),
  );

  readonly applyMutation = injectMutation(() => ({
    mutationFn: (id: string) => lastValueFrom(this.service.apply(id)),
    onSuccess: (result: AdjustmentRequest) => {
      this.notification.success(
        'Adjustment applied',
        `${result.placeName} is now ${result.appliedRadius}m. Every route through it has been updated.`,
      );
      this.refetchAll();
    },
    onError: (error: { error?: { detail?: string } }) => {
      this.logger.error('Applying geofence adjustment failed', error);
      // The server's refusals here are the useful ones — it names the neighbour that makes a
      // conflict unsolvable, or says the conflict has already gone. Never flatten them.
      this.notification.error('Could not apply the adjustment', error?.error?.detail ?? 'Unknown error occurred');
      this.refetchAll();
    },
  }));

  readonly raiseMutation = injectMutation(() => ({
    mutationFn: (placeId: string) => lastValueFrom(this.service.raise(placeId)),
    onSuccess: () => {
      this.notification.success('Request raised', 'It is now in the Requests tab, ready to apply.');
      this.refetchAll();
    },
    onError: (error: { error?: { detail?: string } }) =>
      this.notification.error('Could not raise the request', error?.error?.detail ?? 'Unknown error occurred'),
  }));

  onApply(request: AdjustmentRequest): void {
    this.modal.confirm({
      nzTitle: `Set ${request.placeName} to ${request.recommendedRadius}m?`,
      nzContent:
        'This updates the official place, so every route and bus stop that uses it changes immediately. ' +
        'The figure is recalculated at the moment you confirm.',
      nzOkText: 'Apply change',
      nzOnOk: () => this.applyMutation.mutate(request.id),
    });
  }

  onRaise(conflict: PlaceConflict): void {
    this.raiseMutation.mutate(conflict.standardPlaceId);
  }

  onReject(request: AdjustmentRequest): void {
    this.modal.confirm({
      nzTitle: `Reject the request for ${request.placeName}?`,
      nzContent: 'The overlap stays, and any route through those two stops keeps losing one stop’s footage.',
      nzOkText: 'Reject',
      nzOkDanger: true,
      nzOnOk: () =>
        lastValueFrom(this.service.reject(request.id, 'Rejected by admin')).then(() => this.refetchAll()),
    });
  }

  /** Google Maps, centred on the two stops, for when someone wants to look rather than act. */
  mapLink(conflict: Pick<PlaceConflict, 'placeName'> & { conflicts: Array<{ b: string }> }): string {
    const query = encodeURIComponent(`${conflict.placeName}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  }

  worstOverlap(item: { conflicts: Array<{ overlapMetres: number }> }): number {
    return item.conflicts?.[0]?.overlapMetres ?? 0;
  }

  /**
   * For a place that is NOT the culprit, the wider neighbour worth fixing instead.
   *
   * Both sides of an overlap can be made to clear by shrinking, so this row still shows a
   * perfectly valid number — and acting on it would shrink a stop that was never the problem
   * while leaving the cause in place. Naming the better target turns a tempting button into an
   * informed choice.
   */
  betterTarget(conflict: PlaceConflict): string | null {
    if (conflict.isLikelyCulprit) return null;
    for (const pair of conflict.conflicts) {
      const isA = pair.a === conflict.placeName;
      const mine = isA ? pair.aRadius : pair.bRadius;
      const theirs = isA ? pair.bRadius : pair.aRadius;
      if (theirs > mine) return isA ? pair.b : pair.a;
    }
    return null;
  }

  private refetchAll(): void {
    this.conflictsQuery.refetch();
    this.requestsQuery.refetch();
    this.summaryQuery.refetch();
  }
}
